import React, { useState } from 'react';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Button,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Inventory as InventoryIcon,
  QrCodeScanner as QrCodeIcon,
  SwapHoriz as TransactionIcon,
  Settings as MachineIcon,
  Category as CategoryIcon,
  LocalShipping as SupplierIcon,
  Build as MaintenanceIcon,
  Engineering as TechnicianIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Apps as AppsIcon,
  Description as PRIcon,
  ViewKanban as TrelloIcon,
  ListAlt as BoardListIcon,
  TrendingUp as TrendingUpIcon,
  VerifiedUser as QualityIcon,
  Settings as SettingsIcon,
  Bolt as EnergyIcon,
  LocalLaundryService as SoffIcon,
  LineWeight as BulkIcon,
  DesignServices as CadIcon,
  AccountTree as ArchitectureIcon,
  Input as DataEntryIcon,
  TableChart as MeterReadingsIcon,
  Assessment as AnalyticsIcon,
  Timeline as FlowVisionIcon,
  DeleteSweep as WasteIcon,
  ContentCut as CutWasteIcon,
  Replay as ReprocessingIcon,
  Opacity as DyeIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { getAvatarUrl } from '../services/api';
import NotificationBell from './NotificationBell';

const drawerWidth = 240;

// Define menu items for each system
const sparePartsMenuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/spare-parts-system/', roles: ['admin', 'manager', 'technician', 'viewer'] },
  { text: 'Spare Parts', icon: <InventoryIcon />, path: '/spare-parts-system/spare-parts', roles: ['admin', 'manager', 'technician', 'viewer'] },
  { text: 'QR Scanner', icon: <QrCodeIcon />, path: '/spare-parts-system/qr-scanner', roles: ['admin', 'manager', 'technician', 'viewer'] },
  { text: 'Transactions', icon: <TransactionIcon />, path: '/spare-parts-system/transactions', roles: ['admin', 'manager', 'technician', 'viewer'] },
  { text: 'Machines', icon: <MachineIcon />, path: '/spare-parts-system/machines', roles: ['admin', 'manager'] },
  { text: 'Categories', icon: <CategoryIcon />, path: '/spare-parts-system/categories', roles: ['admin', 'manager'] },
  { text: 'Suppliers', icon: <SupplierIcon />, path: '/spare-parts-system/suppliers', roles: ['admin', 'manager'] },
  { text: 'Maintenance', icon: <MaintenanceIcon />, path: '/spare-parts-system/maintenance', roles: ['admin', 'manager', 'technician'] },
];

const prManagementMenuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/pr-management-system/', roles: ['admin', 'manager', 'technician', 'viewer'] },
  { text: 'Purchase Requisitions', icon: <PRIcon />, path: '/pr-management-system/purchase-requisitions', roles: ['admin', 'manager', 'technician', 'viewer'] },
];

const printingProcessMenuItems = [
  { text: 'Overview', icon: <AppsIcon />, path: '/printing-process-system', exact: true, roles: ['admin'] },
  { text: 'S/OFF Management', icon: <SoffIcon />, path: '/printing-process-system/soff', roles: ['admin'] },
  { text: 'Bulk Management', icon: <BulkIcon />, path: '/printing-process-system/bulk', roles: ['admin'] },
  { text: 'CAD Management', icon: <CadIcon />, path: '/printing-process-system/cad', roles: ['admin'] },
];

const machineEfficiencyMenuItems = [
  { text: 'Machine Efficiency', icon: <DashboardIcon />, path: '/machine-efficiency-system/', roles: ['admin', 'manager', 'technician', 'machine'] },
  { text: 'OEE Dashboard', icon: <TrendingUpIcon />, path: '/machine-efficiency-system/oee-dashboard', roles: ['admin', 'manager', 'technician', 'machine'] },
];

const pfpTrollyMenuItems = [
  { text: 'PFP Trolly Management', icon: <InventoryIcon />, path: '/pfp-trolly-system/', roles: ['admin', 'manager', 'technician'] },
];

const qualityManagementMenuItems = [
  { text: 'Quality Management', icon: <QualityIcon />, path: '/quality-management-system/', roles: ['admin', 'manager', 'technician'] },
];

const maintenanceManagementMenuItems = [
  { text: 'Maintenance Management', icon: <MaintenanceIcon />, path: '/maintenance-management-system/', roles: ['admin', 'manager', 'technician', 'viewer'] },
  { text: 'Preventive Maintenance', icon: <MaintenanceIcon />, path: '/maintenance-management-system/preventive', roles: ['admin', 'manager', 'technician', 'viewer'] },
  { text: 'Predictive Maintenance', icon: <MaintenanceIcon />, path: '/maintenance-management-system/predictive', roles: ['admin', 'manager', 'technician', 'viewer'] },
];

const iotSystemMenuItems = [
  { text: 'IoT Devices', icon: <SettingsIcon />, path: '/iot-system/', roles: ['admin'] },
  { text: 'Fabric Width', icon: <TrendingUpIcon />, path: '/iot-system/fabric-width', roles: ['admin'] },
];

const energyManagementMenuItems = [
  { text: 'Meter Architecture', icon: <ArchitectureIcon />, path: '/energy-management-system/', roles: ['admin', 'manager'] },
  { text: 'FlowVision', icon: <FlowVisionIcon />, path: '/energy-management-system/flowvision', roles: ['admin', 'manager'] },
  { text: 'Data Entry', icon: <DataEntryIcon />, path: '/energy-management-system/data-entry', roles: ['admin', 'manager'] },
  { text: 'Meter Readings', icon: <MeterReadingsIcon />, path: '/energy-management-system/meter-readings', roles: ['admin', 'manager'] },
  { text: 'Analytics & Reports', icon: <AnalyticsIcon />, path: '/energy-management-system/analytics', roles: ['admin', 'manager'] },
];

const wasteManagementMenuItems = [
  { text: 'Waste Management', icon: <WasteIcon />, path: '/waste-management-system/', roles: ['admin', 'manager', 'technician', 'viewer'] },
  { text: 'Cut Waste Management', icon: <CutWasteIcon />, path: '/waste-management-system/cut-waste', roles: ['admin', 'manager', 'technician', 'viewer'] },
];

const dyeStockManagementMenuItems = [
  { text: 'Dye Stock Management', icon: <DyeIcon />, path: '/dye-stock-management-system/', roles: ['admin', 'manager', 'technician', 'viewer'] },
];

const reprocessingManagementMenuItems = [
  { text: 'Reprocessing Management', icon: <ReprocessingIcon />, path: '/reprocessing-management-system/', roles: ['admin', 'manager', 'technician', 'viewer'] },
];

const settingsMenuItems: typeof sparePartsMenuItems = [];

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDesktopDrawerToggle = () => {
    setDesktopOpen(!desktopOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Determine which system we're in based on the current path
  const currentSystem = location.pathname.startsWith('/pr-management-system') 
    ? 'pr-management' 
    : location.pathname.startsWith('/printing-process-system')
    ? 'printing-process'
    : location.pathname.startsWith('/machine-efficiency-system')
    ? 'machine-efficiency'
    : location.pathname.startsWith('/pfp-trolly-system')
    ? 'pfp-trolly'
    : location.pathname.startsWith('/quality-management-system')
    ? 'quality-management'
    : location.pathname.startsWith('/maintenance-management-system')
    ? 'maintenance-management'
    : location.pathname.startsWith('/iot-system')
    ? 'iot-system'
    : location.pathname.startsWith('/energy-management-system')
    ? 'energy-management'
    : location.pathname.startsWith('/waste-management-system')
    ? 'waste-management'
    : location.pathname.startsWith('/dye-stock-management-system')
    ? 'dye-stock-management'
    : location.pathname.startsWith('/reprocessing-management-system')
    ? 'reprocessing-management'
    : location.pathname.startsWith('/settings')
    ? 'settings'
    : 'spare-parts';

  const systemTitle = currentSystem === 'pr-management' 
    ? 'PR Management' 
    : currentSystem === 'printing-process'
    ? 'Printing Process Manager'
    : currentSystem === 'machine-efficiency'
    ? 'Machine Efficiency Management'
    : currentSystem === 'pfp-trolly'
    ? 'PFP Trolly Management'
    : currentSystem === 'quality-management'
    ? 'Quality Management'
    : currentSystem === 'maintenance-management'
    ? 'Maintenance Management'
    : currentSystem === 'iot-system'
    ? 'Wireless Sensor Network'
    : currentSystem === 'energy-management'
    ? 'Energy Management System'
    : currentSystem === 'waste-management'
    ? 'Waste Management System'
    : currentSystem === 'dye-stock-management'
    ? 'Dye Stock Management'
    : currentSystem === 'reprocessing-management'
    ? 'Reprocessing Management'
    : currentSystem === 'settings'
    ? 'Settings'
    : 'Spare Parts Management';

  // Get menu items for current system
  const allMenuItems = currentSystem === 'pr-management' 
    ? prManagementMenuItems 
    : currentSystem === 'printing-process'
    ? printingProcessMenuItems
    : currentSystem === 'machine-efficiency'
    ? machineEfficiencyMenuItems
    : currentSystem === 'pfp-trolly'
    ? pfpTrollyMenuItems
    : currentSystem === 'quality-management'
    ? qualityManagementMenuItems
    : currentSystem === 'maintenance-management'
    ? maintenanceManagementMenuItems
    : currentSystem === 'iot-system'
    ? iotSystemMenuItems
    : currentSystem === 'energy-management'
    ? energyManagementMenuItems
    : currentSystem === 'waste-management'
    ? wasteManagementMenuItems
    : currentSystem === 'dye-stock-management'
    ? dyeStockManagementMenuItems
    : currentSystem === 'reprocessing-management'
    ? reprocessingManagementMenuItems
    : currentSystem === 'settings'
    ? settingsMenuItems
    : sparePartsMenuItems;

  // Filter menu items based on role with allowed_systems override for non-admin users.
  const menuItems = allMenuItems.filter((item) => {
    if (!user) return false;
    if (user.role === 'admin') return true;

    if (user.allowed_systems && user.allowed_systems.length > 0) {
      return user.allowed_systems.includes(currentSystem);
    }

    return item.roles.includes(user.role);
  });

  const drawer = (
    <div>
      <Toolbar sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
          <span style={{ color: '#00695C' }}>O</span>
          <span style={{ color: '#00A9E0' }}>C</span>
          <span style={{ color: '#00695C' }}>E</span>
          <span style={{ color: '#E31E24' }}>A</span>
          <span style={{ color: '#00695C' }}>N </span>
          <span style={{ color: '#00695C' }}>L</span>
          <span style={{ color: '#E31E24' }}>A</span>
          <span style={{ color: '#00695C' }}>N</span>
          <span style={{ color: '#00A9E0' }}>K</span>
          <span style={{ color: '#00695C' }}>A</span>
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{
            fontFamily: "'Brush Script MT', 'Segoe Script', cursive",
            fontStyle: 'italic',
            color: '#00695C',
            fontSize: '0.9rem',
          }}
        >
          Printing Department
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={
                item.path === '/printing-process-system'
                  ? location.pathname === item.path
                  : location.pathname === item.path || location.pathname.startsWith(item.path + '/')
              }
              onClick={() => {
                navigate(item.path);
                setMobileOpen(false);
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: desktopOpen ? `calc(100% - ${drawerWidth}px)` : '100%' },
          ml: { sm: desktopOpen ? `${drawerWidth}px` : 0 },
          transition: 'all 0.3s ease',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <IconButton
            color="inherit"
            aria-label="toggle drawer"
            edge="start"
            onClick={handleDesktopDrawerToggle}
            sx={{ mr: 2, display: { xs: 'none', sm: 'block' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {systemTitle}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AppsIcon />}
              onClick={() => navigate('/')}
              sx={{
                color: 'white',
                borderColor: 'rgba(255, 255, 255, 0.5)',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              All Systems
            </Button>
            <NotificationBell />
            <Typography variant="body2">{user?.full_name}</Typography>
            <IconButton onClick={handleProfileMenuOpen} size="small">
              <Avatar
                src={getAvatarUrl(user?.profile_picture)}
                sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}
              >
                {!user?.profile_picture && (user?.full_name?.charAt(0) || 'U')}
              </Avatar>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
      >
        <MenuItem disabled>
          <PersonIcon sx={{ mr: 1 }} />
          {user?.role?.toUpperCase()}
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => { handleProfileMenuClose(); navigate('/settings'); }}>
          <SettingsIcon sx={{ mr: 1 }} />
          Settings & Profile
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <LogoutIcon sx={{ mr: 1 }} />
          Logout
        </MenuItem>
      </Menu>

      <Box
        component="nav"
        sx={{ 
          width: { sm: desktopOpen ? drawerWidth : 0 }, 
          flexShrink: { sm: 0 },
          transition: 'width 0.3s ease',
        }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="persistent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              transition: 'transform 0.3s ease',
            },
          }}
          open={desktopOpen}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { xs: '100%', sm: desktopOpen ? `calc(100% - ${drawerWidth}px)` : '100%' },
          maxWidth: '100vw',
          minHeight: '100vh',
          bgcolor: '#f5f5f5',
          overflow: 'auto',
          transition: 'all 0.3s ease',
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
